import type { ReactNode } from 'react';
import type {
  FmTableColumn,
  FmTableGroup,
  FmTableGroupBy,
  FmTableResolvedGroup,
  FmTableSortDirection,
  FmTableSortValue,
} from '../../types/fm-table';

export type SortDirection = FmTableSortDirection;
export type SortState = { key: string; direction: SortDirection } | null;

export type FmTableInternalRow<T> =
  | { kind: 'data'; key: string; row: T }
  | { kind: 'group'; key: string; group: FmTableResolvedGroup<T> };

export const cx = (...parts: Array<string | false | null | undefined>): string =>
  parts.filter(Boolean).join(' ');

export function isGroupRow<T>(
  row: FmTableInternalRow<T>,
): row is Extract<FmTableInternalRow<T>, { kind: 'group' }> {
  return row.kind === 'group';
}

function resolveGroup<T>(
  row: T,
  groupBy: FmTableGroupBy<T>,
  ungroupedLabel: ReactNode,
): FmTableGroup {
  if (typeof groupBy === 'function') {
    const group = groupBy(row);
    if (typeof group === 'object' && group !== null) {
      return group;
    }
    const key = String(group);
    return { key, label: key };
  }

  const value = (row as Record<string, unknown>)[groupBy];
  const key = value === null || value === undefined || value === ''
    ? 'ungrouped'
    : String(value);
  return { key, label: key === 'ungrouped' ? ungroupedLabel : key };
}

export function buildInternalRows<T>(
  rows: T[],
  rowKey: (row: T) => string,
  groupBy: FmTableGroupBy<T> | undefined,
  ungroupedLabel: ReactNode,
): FmTableInternalRow<T>[] {
  if (!groupBy) {
    return rows.map((row) => ({ kind: 'data', key: rowKey(row), row }));
  }

  const groups: FmTableResolvedGroup<T>[] = [];
  const groupIndex = new Map<string, FmTableResolvedGroup<T>>();

  rows.forEach((row) => {
    const group = resolveGroup(row, groupBy, ungroupedLabel);
    const existing = groupIndex.get(group.key);
    if (existing) {
      existing.rows.push(row);
      return;
    }
    const resolvedGroup: FmTableResolvedGroup<T> = { key: group.key, label: group.label, rows: [row] };
    groups.push(resolvedGroup);
    groupIndex.set(group.key, resolvedGroup);
  });

  return groups.flatMap((group) => [
    { kind: 'group' as const, key: `__fm-group-${group.key}`, group },
    ...group.rows.map((row) => ({ kind: 'data' as const, key: rowKey(row), row })),
  ]);
}

export function columnLabel(header: ReactNode, fallback: string): string {
  if (typeof header === 'string' || typeof header === 'number') return String(header);
  return fallback;
}

function resolveSortValue<T>(row: T, column: FmTableColumn<T>): FmTableSortValue {
  if (column.sortValue) return column.sortValue(row);
  const value = (row as Record<string, unknown>)[column.key];
  if (
    typeof value === 'string'
    || typeof value === 'number'
    || typeof value === 'boolean'
    || value instanceof Date
    || value === null
    || value === undefined
  ) {
    return value;
  }
  return String(value);
}

function compareSortValues(aVal: FmTableSortValue, bVal: FmTableSortValue): number {
  if (aVal instanceof Date && bVal instanceof Date) {
    return aVal.getTime() - bVal.getTime();
  }
  if (typeof aVal === 'number' && typeof bVal === 'number') return aVal - bVal;
  if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
    return Number(aVal) - Number(bVal);
  }
  return String(aVal ?? '').localeCompare(String(bVal ?? ''));
}

function compareColumnValues<T>(a: T, b: T, column: FmTableColumn<T>): number {
  if (column.sorter) return column.sorter(a, b);
  return compareSortValues(resolveSortValue(a, column), resolveSortValue(b, column));
}

export function sortRows<T>(
  rows: T[],
  columns: FmTableColumn<T>[],
  sortState: SortState,
): T[] {
  if (!sortState) return rows;
  const column = columns.find((candidate) => candidate.key === sortState.key);
  if (!column) return rows;

  const multiplier = sortState.direction === 'ascending' ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const result = compareColumnValues(a.row, b.row, column);
      return result === 0 ? a.index - b.index : result * multiplier;
    })
    .map(({ row }) => row);
}

export function normalizeKeys(keys: string[] | undefined): Set<string> {
  return new Set((keys ?? []).map(String));
}

export function expansionContentId(tableId: string, key: string): string {
  return `${tableId}-expanded-${key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

export function shouldRenderSummary(summaryContent: ReactNode): boolean {
  return summaryContent !== null && summaryContent !== undefined && summaryContent !== false;
}
